/**
 * Installs the demo API: patches `globalThis.fetch` so any request whose path
 * starts with `/api/` is answered in-process by `./router` instead of going to
 * the network. Everything else passes straight through to the real `fetch`.
 *
 * One seam, chosen deliberately. The app reaches Pages Functions two ways —
 * `apiSend()` in src/lib/api/_client.ts and ~29 bare `fetch('/api/…')` calls
 * scattered through components — and intercepting here covers both without
 * touching a single component.
 *
 * Importing this module installs it (see src/main.tsx, where it is the first
 * import). Idempotent.
 */
import { route } from './router';

type FetchInput = Parameters<typeof globalThis.fetch>[0];
type FetchInit = Parameters<typeof globalThis.fetch>[1];

let installed = false;

export function installDemoApi(): void {
  if (installed) return;
  installed = true;

  const realFetch = globalThis.fetch.bind(globalThis);
  const origin = globalThis.location?.origin ?? 'http://localhost';

  globalThis.fetch = async (input: FetchInput, init?: FetchInit): Promise<Response> => {
    const href =
      typeof input === 'string' ? input
      : input instanceof URL ? input.href
      : input.url;
    const url = new URL(href, origin);
    if (!url.pathname.startsWith('/api/')) return realFetch(input, init);

    const method = (init?.method ?? (input instanceof Request ? input.method : 'GET')).toUpperCase();

    let body: unknown;
    const raw = init?.body ?? (input instanceof Request ? await input.clone().text() : undefined);
    if (typeof raw === 'string' && raw.length) {
      try { body = JSON.parse(raw); } catch { body = raw; }
    }

    return route({ method, path: url.pathname, url, body });
  };
}

installDemoApi();
